/**
 * Operator landing (persona-gated from Home; route: /production/operator).
 *
 * Promoted verbatim from the /dev/operator-home v4 prototype — same kiosk-tile
 * layout, grounded in the 2026-07 competitor survey (OPERATOR_EXPERIENCE_
 * DESIGN.md §6). What's LIVE vs PREVIEW today:
 *
 *  LIVE:    greeting · Scan (real WO/part resolve → WO detail) · In progress
 *           (my open StepExecutions via /api/StepExecutions/my_workload/;
 *           Resume → the operator runtime for that run) · clock cluster
 *           (TimeEntry clock_in/out; Break/Lunch pause labor as BREAK/LUNCH entries)
 *           and My hours this week · Notifications (the in-app feed; "Got it" =
 *           mark-read) · Shift notes · UP NEXT + THEN (server-ranked WO×step rows
 *           from /api/WorkQueue/, filtered by kind=PRODUCTION and the user's
 *           WorkCenter memberships; Start navigates to WO detail — the operator work
 *           surface) · station-scope combobox (memberships → picked station persists
 *           per-user in localStorage) · day recap (StepExecutions/my_day) ·
 *           first-piece strip (FPIRecords/mine) · Report a problem → machine (the
 *           downtime dialog, my station's machines) · undo strip (my last finished
 *           step: Undo = Parts rollback, Flag = a new quality report on that part).
 *  PREVIEW: dimmed tiles, by decision — Call my lead; Can't run this? and Your flags
 *           (blocker routing not built, 10/21); the job branch of Report a problem.
 *           Each renders placeholder content behind a blur+disable via <PreviewLock>,
 *           so the planned surface is legible without pretending it's wired.
 *
 * As each backend lands (queue aggregate first), swap the matching PreviewLock
 * for real data.
 */
import { Combobox } from "@/components/ui/combobox";
import { useEffect, useMemo, useState } from "react";
import { InFlightPicker, inFlightOptions, useOpenInFlight, waitingLine } from "@/components/operator/InFlightPicker";
import { toast } from "sonner";
import { Link, useNavigate } from "@tanstack/react-router";
import { queryOptions, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, BookOpen, Clock, Hand, Lock, Megaphone, PlayCircle, ScanLine, StickyNote, Timer, Wrench, XCircle } from "lucide-react";

import { api } from "@/lib/api/generated";
import { useAuthUser, type AuthUser } from "@/hooks/useAuthUser";
import { PersonaSwitcher } from "@/components/home/PersonaSwitcher";
import { ScanBox } from "@/components/home/home-blocks";
import { useNotificationFeed, useMarkNotificationRead } from "@/hooks/notificationFeed";
import { useActiveShiftNotes, useAcknowledgeShiftNote, type ShiftNote } from "@/hooks/shiftNotes";
import { useWorkQueue, type WorkQueueRow } from "@/hooks/workQueue";
import { matchKey } from "@/lib/query-filters";
import { useMyHours } from "@/hooks/useScheduling";
import { ReportExceptionDialog } from "@/pages/workorders/ReportExceptionDialog";
import { ReasonActionButton } from "@/components/common/ReasonActionButton";
import { getCookie } from "@/lib/utils";

/** The operator's own open runs, scoped to production surface + station. */
const myWorkloadOptions = (scopedWcIds: string[]) =>
    queryOptions({
        queryKey: ["step-executions", "my-workload", "PRODUCTION", scopedWcIds.slice().sort().join(",")],
        queryFn: () => api.api_StepExecutions_my_workload_list({
            queries: {
                limit: 5,
                step__work_center__kind: "PRODUCTION",
                ...(scopedWcIds.length ? { step__work_center__in: scopedWcIds } : {}),
            },
        }),
    });

/** My day: completions + flags today, and my last completed step (the undo strip). */
const myDayOptions = () =>
    queryOptions({
        queryKey: ["step-executions", "my-day"],
        queryFn: () => api.api_StepExecutions_my_day_retrieve(),
        refetchInterval: 60_000,
    });

/** First pieces from my runs — open, or decided today. */
const myFirstPiecesOptions = () =>
    queryOptions({
        queryKey: ["fpi-records", "mine"],
        queryFn: () => api.api_FPIRecords_mine_list(),
        refetchInterval: 30_000,
    });

const FIRST_PIECE_ROWS = 3;

const FPI_VERDICT: Record<string, { label: string; className: string }> = {
    PASSED: { label: "Passed", className: "font-medium text-emerald-700 dark:text-emerald-400" },
    FAILED: { label: "Failed", className: "font-medium text-red-700 dark:text-red-400" },
    WAIVED: { label: "Waived", className: "font-medium text-amber-700 dark:text-amber-400" },
};

/** Clock state: my one open time entry (end_time is null). */
const openTimeEntryOptions = (userPk: AuthUser["pk"]) =>
    queryOptions({
        queryKey: ["time-entries", "open", userPk],
        queryFn: () => api.api_TimeEntries_list({
            queries: { user: userPk, end_time__isnull: true, ordering: "-start_time", limit: 1 },
        }),
    });

// ---------------------------------------------------------------------------
// MOCK — drives the PREVIEW (dimmed) tiles so the planned layout is legible.
// Shapes are the wishlist for the real backing (queue aggregate, blocker model,
// shift-notes model). LIVE tiles below don't touch this.
// ---------------------------------------------------------------------------

type MyFlag = {
    id: string; label: string; wo: string; owner: string;
    elapsed: string; seenBy: string | null;
};

// Layout placeholders for the Preview tiles. Deliberately not names or record numbers:
// a trainee on the training tenant went looking for "Dana" and "WO-2024-0047".
const MOCK = {
    // Flags I raised — lifecycle visible so nobody walks the floor to ask "did anyone see this?"
    myFlags: [
        { id: "f0", label: "<reason>", wo: "<work order>", owner: "your lead", elapsed: "—", seenBy: null },
    ] as MyFlag[],
};

// Blocker reasons double as the routing table (reason → owner queue).
const BLOCK_REASONS = [
    { key: "material", label: "Missing material", owner: "Materials" },
    { key: "tooling", label: "Tooling / fixture unavailable", owner: "your lead" },
    { key: "quality", label: "Quality hold", owner: "QA" },
    { key: "other", label: "Something else", owner: "your lead" },
] as const;

// The machine branch's quick reasons, each opening the downtime dialog prefilled.
const MACHINE_REASONS = [
    { label: "Machine down / fault", category: "UNPLANNED" },
    { label: "Needs maintenance", category: "UNPLANNED" },
    { label: "Setup / changeover issue", category: "CHANGEOVER" },
    { label: "Other", category: "OTHER" },
] as const;

const say = (msg: string) => toast.info(msg, { description: "Preview — this tile isn't wired to live data yet." });

/** A kiosk tile: bordered, filled, generous padding, whole-tile affordance. */
function Tile({ className = "", children }: { className?: string; children: React.ReactNode }) {
    return <div className={`rounded-xl border bg-card p-4 ${className}`}>{children}</div>;
}

/** Wraps a tile whose backend isn't built yet: dims + desaturates + disables it
 *  and stamps a "Preview" chip, so the planned layout reads as intentional, not
 *  broken. Grid span goes on `className`; the child keeps its own styling. */
function PreviewLock({ className = "", children }: { className?: string; children: React.ReactNode }) {
    return (
        <div className={`relative ${className}`}>
            <div className="pointer-events-none select-none opacity-45 saturate-[.5]">
                {children}
            </div>
            <Badge
                variant="secondary"
                className="absolute right-2 top-2 gap-1 border bg-background/80 text-[10px] font-medium shadow-sm backdrop-blur-sm"
            >
                <Lock className="h-3 w-3" /> Preview
            </Badge>
        </div>
    );
}

/** Route wrapper (/production/operator) — Home renders OperatorHomePage directly
 *  for the Operator persona; the named route lets leads/admins reach it too. */
export function OperatorHomeRoute() {
    const { data: user } = useAuthUser();
    if (!user) return null;
    return <OperatorHomePage user={user} />;
}

export function OperatorHomePage({ user }: { user: AuthUser }) {
    const navigate = useNavigate();

    // Station-scope — the operator's picked work-center, scoped to their
    // memberships. Persisted client-side keyed by user pk. "all" = "all my
    // stations" (union of memberships). See Documents/WORK_CENTER_DESIGN.md
    // Phase 2. Users with no memberships fall through to unscoped by kind only.
    // useMemo, not a bare `?? []`: the fallback builds a NEW array on every
    // render while data is undefined, so the memo below never actually memoised.
    const memberships = useMemo(() => user.work_center_memberships ?? [], [user.work_center_memberships]);
    const primaryWcId = memberships.find((m) => m.is_primary)?.work_center;
    const storageKey = `operator.activeWc.${user.pk ?? "anon"}`;
    const [activeWcId, setActiveWcId] = useState<string>(() => {
        const stored = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;
        return stored ?? primaryWcId ?? "all";
    });
    // If memberships load later and we defaulted before, promote to primary once.
    useEffect(() => {
        if (activeWcId === "all" && primaryWcId
            && typeof window !== "undefined"
            && !window.localStorage.getItem(storageKey)) {
            setActiveWcId(primaryWcId);
        }
    }, [primaryWcId]);  // eslint-disable-line react-hooks/exhaustive-deps
    const setActiveWc = (id: string) => {
        setActiveWcId(id);
        if (typeof window !== "undefined") window.localStorage.setItem(storageKey, id);
    };
    // The WC ids to send as `work_center__in`: [] means "don't filter"; a
    // single id means "just this station"; multiple = union of my stations.
    const scopedWcIds = useMemo<string[]>(() => {
        if (memberships.length === 0) return [];
        if (activeWcId === "all") return memberships.map((m) => m.work_center);
        return [activeWcId];
    }, [memberships, activeWcId]);

    // Machine problem → the downtime dialog, limited to my station's machines (all my
    // stations when scoped to "all"; none known → any machine).
    const [downtime, setDowntime] = useState<(typeof MACHINE_REASONS)[number] | null>(null);
    const { data: workCenters } = useQuery({
        queryKey: ["work-centers", "for-downtime"],
        queryFn: () => api.api_WorkCenters_list({ queries: { limit: 500 } }),
        // Loaded with the page, so the station's machines are ready when the dialog opens.
        staleTime: 5 * 60_000,
    });
    const stationEquipmentIds = useMemo(() => {
        const wcs = (workCenters?.results ?? []).filter((w) => scopedWcIds.includes(String(w.id)));
        return [...new Set(wcs.flatMap((w) => (w.equipment ?? []).map(String)))];
    }, [workCenters, scopedWcIds]);

    // LIVE — the operator's own open runs, scoped to production surface + station.
    const { data: workload } = useQuery(myWorkloadOptions(scopedWcIds));
    const runs = workload?.results ?? [];
    // Everything I have open — part visits and loads, any station — for Resume's picker.
    const { data: inFlight } = useQuery(inFlightOptions());
    const [pickerOpen, setPickerOpen] = useState(false);
    const resumable = inFlight?.items ?? [];
    const resumeNext = resumable[0] ?? null;
    const openInFlight = useOpenInFlight();

    // LIVE — clock state: my one open time entry (end_time is null).
    const queryClient = useQueryClient();
    const { data: openEntryPage, isSuccess: clockKnown } = useQuery(openTimeEntryOptions(user.pk));
    const { data: myHours } = useMyHours();
    const { data: myDay } = useQuery(myDayOptions());
    const lastFinished = myDay?.last_finished ?? null;
    const { data: firstPieces = [] } = useQuery(myFirstPiecesOptions());
    // Server order: pending (oldest first), then decided (newest first).
    const [showAllFirstPieces, setShowAllFirstPieces] = useState(false);
    const openEntry = openEntryPage?.results?.[0] ?? null;
    const onLunch = openEntry?.entry_type === "LUNCH";
    const paused = openEntry?.entry_type === "BREAK" || onLunch;
    // Unknown is not "out": a failed or pending fetch (a server reload) read as Not
    // clocked in and offered Clock in to someone already on the clock (walk 2026-10-22).
    const clockText = !clockKnown
        ? "Checking the clock…"
        : !openEntry
        ? "Not clocked in"
        : paused
            ? `On ${onLunch ? "lunch" : "break"} · ${formatDistanceToNow(new Date(openEntry.start_time))}`
            : `Clocked in · ${formatDistanceToNow(new Date(openEntry.start_time))}`;

    // The clock as a one-open-entry state machine (TimeEntry is the labor-costing
    // record): clock in → SHIFT; Break/Lunch close the open entry then open a
    // BREAK/LUNCH pause; End break resumes SHIFT; Clock out closes and reopens
    // nothing. Job time isn't clocked: it's derived from the step records the operator
    // works (services/mes/labor_report.py), so the clock is attendance only.
    // Must-acknowledge shift notes are read at clock-in (audit row 11): clocking in
    // opens them, and the dialog closes once each is acknowledged.
    const [ackAtClockIn, setAckAtClockIn] = useState(false);
    // Clocking out with a step still running leaves that run open on the floor: say so
    // first (Operator walk — it went straight through).
    const clockOut = () => {
        const running = runs.find((r) => r.status === "IN_PROGRESS");
        if (running && !window.confirm(
            `${running.step_name ?? "A step"}${running.part_erp_id ? ` on ${running.part_erp_id}` : ""} is still running. ` +
            "Clocking out leaves it open for the next shift to pick up. Clock out anyway?")) return;
        clockAction.mutate({ kind: "clockout" });
    };
    const clockAction = useMutation({
        mutationFn: async (
            action:
                | { kind: "clockin" }
                | { kind: "pause"; type: "BREAK" | "LUNCH" }
                | { kind: "resume" }
                | { kind: "clockout" }
        ) => {
            if (action.kind !== "clockin" && openEntry) {
                await api.api_TimeEntries_clock_out_create({}, { params: { id: String(openEntry.id) } });
            }
            if (action.kind === "clockin" || action.kind === "resume") {
                return api.api_TimeEntries_clock_in_create({ entry_type: "SHIFT" });
            }
            if (action.kind === "pause") {
                return api.api_TimeEntries_clock_in_create({ entry_type: action.type });
            }
            return null; // clockout — nothing reopened
        },
        onSuccess: (_data, action) => {
            queryClient.invalidateQueries(matchKey(["time-entries", "open", user.pk]));
            queryClient.invalidateQueries({ queryKey: ["operator-hours", "mine"] });
            if (action.kind === "clockin") {
                toast.success("Clocked in.");
                setAckAtClockIn(true);
            }
            else if (action.kind === "pause") toast.success(action.type === "LUNCH" ? "On lunch — labor paused." : "On break — labor paused.");
            else if (action.kind === "resume") toast.success("Welcome back — back on shift.");
            else toast.success("Clocked out.");
        },
        onError: () => toast.error("Couldn't update your clock — try again."),
    });

    // LIVE — inbound awareness: the in-app notification feed (unread only).
    // "Got it" marks the item read (shared cache with the header bell).
    const { data: feedItems } = useNotificationFeed({ unread: true, limit: 8 });
    const markRead = useMarkNotificationRead();
    const unread = feedItems ?? [];

    // LIVE — human-authored shift notes active for this operator; "Got it" acks.
    const { data: shiftNotes = [] } = useActiveShiftNotes();
    const ackShiftNote = useAcknowledgeShiftNote();

    // LIVE — the work queue. UP NEXT = first ready row; THEN = next few; the
    // "N blocked" caption counts is_held rows. Rank is server-side (priority →
    // due → aging), so we just consume in order.
    const { data: queueRows = [] } = useWorkQueue({
        kind: "PRODUCTION",
        workCenterIds: scopedWcIds,
        limit: 20,
    });
    const readyRows = queueRows.filter((r) => r.readiness === "ready");
    // Work orders the machine may have been running: my open runs first, then my
    // station's queue. With exactly one open run, its work order is preselected.
    const downtimeWorkOrders = useMemo(() => {
        const seen = new Map<string, string>();
        for (const r of runs) {
            if (r.work_order && !seen.has(String(r.work_order))) {
                seen.set(String(r.work_order), r.work_order_erp_id ?? String(r.work_order));
            }
        }
        for (const r of queueRows) {
            if (r.work_order && !seen.has(String(r.work_order))) {
                seen.set(String(r.work_order), r.work_order_erp_id ?? String(r.work_order));
            }
        }
        return [...seen].map(([id, erp_id]) => ({ id, erp_id }));
    }, [runs, queueRows]);
    const runWorkOrders = [...new Set(runs.map((r) => r.work_order).filter(Boolean).map(String))];
    const heroRow: WorkQueueRow | undefined = readyRows[0];
    const thenRows = readyRows.slice(1, 7);
    const blockedRows = queueRows.filter((r) => r.readiness === "blocked");
    // WorkOrder.priority is IntegerChoices (1=Urgent, 2=High, 3=Normal, 4=Low —
    // the number IS the sort rank).
    const priorityLabel = (n: number | null | undefined) =>
        n === 1 ? "urgent" : n === 2 ? "high" : n === 3 ? "normal" : n === 4 ? "low" : null;
    const priorityTone = (n: number | null | undefined) =>
        n === 1 ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
            : n === 2 ? "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
                : null;
    const mustAckNotes = shiftNotes.filter((n) => n.acknowledgment_required);
    const infoNotes = shiftNotes.filter((n) => !n.acknowledgment_required);
    const noteRow = (n: ShiftNote) => (
        <div key={n.id} className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
                {n.priority === "HIGH" && (
                    <Badge variant="destructive" className="mr-1 align-middle text-[10px]">High</Badge>
                )}
                <b>{n.author_name ?? "Lead"}:</b> {n.body}
                {n.work_order_erp_id ? (
                    <span className="ml-1 font-mono text-xs text-muted-foreground">· {n.work_order_erp_id}</span>
                ) : null}
            </div>
            <Button
                size="sm" variant="ghost" className="h-7 shrink-0 px-2 text-xs"
                disabled={ackShiftNote.isPending}
                onClick={() => ackShiftNote.mutate(n.id)}
            >
                Got it
            </Button>
        </div>
    );

    // PREVIEW state — drives the dimmed tiles so the layout is legible.
    const [flags] = useState<MyFlag[]>(MOCK.myFlags);
    const [blockOpen, setBlockOpen] = useState(false);
    const [problemOpen, setProblemOpen] = useState(false);
    const [problemBranch, setProblemBranch] = useState<"machine" | "job" | null>(null);
    // Combobox label — either "All my stations", a specific station, or a
    // gentle empty state when the user has no memberships yet.
    const areaLabel = memberships.length === 0
        ? "No stations assigned"
        : activeWcId === "all"
            ? `All my stations (${memberships.length})`
            : (memberships.find((m) => m.work_center === activeWcId)?.work_center_name
                ?? "All my stations");

    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

    /** The deviation flow — Preview. The dialog + toast are wired; actually
     *  writing a blocker needs the blocker aggregate that doesn't exist yet. */
    const blockUpNext = (reason: (typeof BLOCK_REASONS)[number]) => {
        setBlockOpen(false);
        toast.info(`Would flag ${heroRow?.step_name ?? "this step"} — ${reason.owner} notified.`, {
            description: "Preview — the blocker aggregate isn't wired yet.",
        });
    };

    return (
        <div className="mx-auto max-w-5xl space-y-3 p-4">
            {/* Header — identity is live; scope + clock are preview. */}
            <div className="flex items-center gap-3">
                <h1 className="min-w-0 flex-1 truncate text-2xl font-semibold tracking-tight">
                    {greeting}{user.first_name ? `, ${user.first_name}` : ""}
                </h1>
                <PersonaSwitcher user={user} />

                {/* Station scope — still Preview (needs the queue aggregate's per-scope counts). */}
                {/* Station scope — LIVE: driven by the user's WorkCenter memberships
                    (see Documents/WORK_CENTER_DESIGN.md Phase 2). Picked station
                    persists per-user in localStorage. */}
                <Combobox
                    className="h-12 w-56 shrink-0"
                    contentClassName="w-64"
                    value={activeWcId}
                    onChange={(v) => v && setActiveWc(v)}
                    options={memberships.length === 0 ? [] : [
                        { value: "all", label: "All my stations", description: `${memberships.length} work centers` },
                        ...memberships.map((m) => ({
                            value: m.work_center, label: m.work_center_name,
                            keywords: [m.work_center_code],
                            ...(m.is_primary ? { description: "primary" } : {}),
                        })),
                    ]}
                    selectedLabel={areaLabel}
                    disabled={memberships.length === 0}
                    placeholder={areaLabel}
                    searchPlaceholder="Work center…"
                    emptyText="Nothing matches."
                />
                {/* Clock cluster — LIVE: TimeEntry clock_in/out; Break/Lunch pause labor. */}
                <div className="flex shrink-0 items-center gap-3">
                    {/* My hours this week — the same figures a lead sees on Operator Hours.
                        Read-only; a wrong clock time is corrected by a lead. */}
                    {myHours && (
                        <span className="hidden text-xs text-muted-foreground lg:inline"
                            title="This week, from your clock and your step records — the same numbers your lead sees on Operator Hours. Ask a lead to correct a clock time.">
                            This week: <b className="text-foreground tabular-nums">{myHours.on_shift_hours.toFixed(1)}</b> on shift
                            {" · "}<b className="text-foreground tabular-nums">{myHours.direct_hours.toFixed(1)}</b> on jobs
                            {myHours.rework_hours > 0 && <> ({myHours.rework_hours.toFixed(1)} rework)</>}
                            {myHours.awaiting_approval > 0 && <>{" · "}{myHours.awaiting_approval} awaiting approval</>}
                        </span>
                    )}
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" /> {clockText}
                    </span>
                    {!clockKnown ? null : !openEntry ? (
                        <Button size="lg" className="h-12" disabled={clockAction.isPending}
                            onClick={() => clockAction.mutate({ kind: "clockin" })}>
                            Clock in
                        </Button>
                    ) : paused ? (
                        <>
                            <Button variant="outline" size="lg" className="h-12" disabled={clockAction.isPending}
                                onClick={() => clockAction.mutate({ kind: "resume" })}>
                                End {onLunch ? "lunch" : "break"}
                            </Button>
                            <Button variant="ghost" size="lg" className="h-12" disabled={clockAction.isPending}
                                onClick={clockOut}>
                                Clock out
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" size="lg" className="h-12" disabled={clockAction.isPending}
                                onClick={() => clockAction.mutate({ kind: "pause", type: "BREAK" })}>
                                Break
                            </Button>
                            <Button variant="outline" size="lg" className="h-12" disabled={clockAction.isPending}
                                onClick={() => clockAction.mutate({ kind: "pause", type: "LUNCH" })}>
                                Lunch
                            </Button>
                            <Button variant="outline" size="lg" className="h-12" disabled={clockAction.isPending}
                                onClick={clockOut}>
                                Clock out
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Orient the viewer: what's real vs. planned. */}
            <p className="text-xs text-muted-foreground">
                Preview build — <b className="text-foreground">Up next</b>, <b className="text-foreground">Then</b>, <b className="text-foreground">Scan</b>, <b className="text-foreground">In progress</b>, <b className="text-foreground">Notifications</b>, <b className="text-foreground">Shift notes</b>, the <b className="text-foreground">station-scope</b> combobox, and the <b className="text-foreground">clock</b> (in/out/break/lunch) the <b className="text-foreground">day recap</b>, the <b className="text-foreground">first-piece strip</b>, <b className="text-foreground">Report a problem</b> (machine) and the <b className="text-foreground">undo strip</b> are live.
                Dimmed tiles show the planned layout; they light up as the blocker model and other pieces land.
            </p>

            {/* FIRST-PIECE loop — LIVE: first pieces from my runs, open or decided today
                (FPIRecords/mine): sent → seen by QA → the verdict. Hidden when none.
                At most three rows, pending first, "+N more" for the rest (user decision
                10/29), so a busy day doesn't push Up next off a kiosk screen. */}
            {firstPieces.length > 0 && (
                <div className="space-y-1">
                    {(showAllFirstPieces ? firstPieces : firstPieces.slice(0, FIRST_PIECE_ROWS)).map((f) => {
                        const pending = f.status === "PENDING";
                        const verdict = FPI_VERDICT[f.status];
                        return (
                            <div key={f.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${pending
                                ? "border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950"
                                : "bg-muted/30"}`}>
                                <Timer className={`h-4 w-4 shrink-0 ${pending ? "text-orange-600" : "text-muted-foreground"}`} />
                                <span className="min-w-0 truncate">
                                    <b>{f.step_name}</b>
                                    {f.part_erp_id ? <> · <span className="font-mono">{f.part_erp_id}</span></> : null}
                                    {" · "}first piece sent {formatDistanceToNow(new Date(f.sent_at))} ago
                                </span>
                                <span className="ml-auto shrink-0 text-muted-foreground">
                                    {pending
                                        ? (f.acknowledged_by_name ? `Seen by ${f.acknowledged_by_name}` : "Not seen yet")
                                        : <span className={verdict?.className}>{verdict?.label ?? f.status}{f.decided_by_name ? ` by ${f.decided_by_name}` : ""}</span>}
                                </span>
                            </div>
                        );
                    })}
                    {firstPieces.length > FIRST_PIECE_ROWS && (
                        <button
                            className="text-xs text-muted-foreground underline-offset-4 hover:underline"
                            onClick={() => setShowAllFirstPieces((v) => !v)}
                        >
                            {showAllFirstPieces ? "Show fewer" : `+${firstPieces.length - FIRST_PIECE_ROWS} more`}
                        </button>
                    )}
                </div>
            )}

            {/* Tile grid — Up Next dominates (2×2); everything else is one tile. */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                {/* UP NEXT — LIVE: the server-ranked top ready row from the
                    work queue. Start navigates to the WO detail page (design
                    doc §8: Start-Work + traveler live there). "Can't run this?"
                    stays Preview — the blocker aggregate isn't built yet. */}
                <Tile className="md:col-span-2 md:row-span-2 h-full border-2 border-primary/40 flex flex-col">
                    {heroRow ? (
                        <>
                            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                Up next
                                {priorityTone(heroRow.priority) && (
                                    <Badge className={priorityTone(heroRow.priority) ?? ""}>
                                        {priorityLabel(heroRow.priority)}
                                    </Badge>
                                )}
                                <span className="ml-auto">
                                    {heroRow.scheduled_start ? `planned ${new Date(heroRow.scheduled_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · ` : ""}
                                    {heroRow.earliest_entered_at ? `ready ${formatDistanceToNow(new Date(heroRow.earliest_entered_at))}` : ""}
                                    {heroRow.expected_completion ? ` · due ${heroRow.expected_completion}` : ""}
                                </span>
                            </div>
                            <div className="mt-3 flex-1">
                                <div className="text-3xl font-semibold leading-tight">{heroRow.step_name}</div>
                                <div className="mt-1 text-sm text-muted-foreground">
                                    {heroRow.part_type_name ? <>{heroRow.part_type_name} · </> : null}
                                    <span className="font-mono">{heroRow.work_order_erp_id}</span>
                                    {heroRow.machine_name ? <> · on <span className="font-medium text-foreground">{heroRow.machine_name}</span></> : null}
                                </div>
                                <div className="mt-2 text-sm text-muted-foreground">
                                    <b className="text-foreground">{heroRow.qty_ready}</b> pc{heroRow.qty_ready === 1 ? "" : "s"} waiting
                                </div>
                            </div>
                            <div className="mt-4 flex gap-2">
                                <Button size="lg" className="h-16 flex-1 text-lg"
                                    onClick={() => navigate({
                                        to: "/workorder/$workOrderId",
                                        params: { workOrderId: String(heroRow.work_order) },
                                    })}>
                                    <PlayCircle className="mr-2 h-6 w-6" /> Start
                                </Button>
                                <Button size="lg" variant="outline" className="h-16" onClick={() => say("Would open the work instructions for this step")}>
                                    <BookOpen className="mr-2 h-5 w-5" /> Instructions
                                </Button>
                            </div>
                            <button
                                className="mt-2 self-start text-sm text-muted-foreground underline-offset-4 hover:underline"
                                onClick={() => setBlockOpen(true)}
                            >
                                Can't run this?
                            </button>
                        </>
                    ) : (
                        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                            <Wrench className="h-8 w-8" />
                            <p className="text-sm font-medium text-foreground">Nothing ready right now</p>
                            {blockedRows.length > 0 && (
                                <p className="max-w-xs text-xs">
                                    {blockedRows.length} job{blockedRows.length === 1 ? "" : "s"} blocked — waiting on holds to clear.
                                </p>
                            )}
                        </div>
                    )}
                </Tile>

                {/* RESUME — LIVE: what I started and haven't finished (services/mes/in_flight):
                    started visits not done on my side, and my loads not yet completed. Not
                    queued work, not work waiting on QA, not the Ship step. */}
                <Tile className="flex flex-col justify-between">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        <Timer className="h-3.5 w-3.5 text-blue-600" /> In progress
                    </div>
                    {resumeNext ? (
                        <>
                            <div className="my-2 min-w-0">
                                <div className="truncate font-medium">{resumeNext.step_name}</div>
                                <div className="truncate font-mono text-xs text-muted-foreground">
                                    {resumeNext.kind === "load"
                                        ? `Load · ${resumeNext.part_count} part${resumeNext.part_count === 1 ? "" : "s"}`
                                        : resumeNext.part_erp_id ?? "—"}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {resumeNext.kind === "load"
                                        ? (resumeNext.sealed_at
                                            ? `Running · sealed ${formatDistanceToNow(new Date(resumeNext.sealed_at))} ago`
                                            : `Loading · started ${formatDistanceToNow(new Date(resumeNext.started_at ?? resumeNext.entered_at ?? Date.now()))} ago`)
                                        : `Running · started ${formatDistanceToNow(new Date(resumeNext.started_at ?? Date.now()))} ago`}
                                    {resumable.length > 1 ? ` · +${resumable.length - 1} more to finish` : ""}
                                </div>
                            </div>
                            <Button
                                className="h-12 w-full"
                                // Several to finish: pick one. One: straight in.
                                onClick={() => resumable.length > 1 ? setPickerOpen(true) : void openInFlight(resumeNext)}
                            >
                                Resume
                            </Button>
                        </>
                    ) : (
                        <>
                            <p className="my-2 text-sm text-muted-foreground">
                                Nothing left unfinished. Scan a traveler to start work.
                            </p>
                            <Button className="h-12 w-full" variant="outline" disabled>Resume</Button>
                        </>
                    )}
                    {/* Done on my side and waiting on someone else: counted, not resumed. */}
                    {(inFlight?.waiting ?? 0) > 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">{waitingLine(inFlight!)}</p>
                    )}
                    <InFlightPicker open={pickerOpen} onOpenChange={setPickerOpen} />
                </Tile>

                {/* SCAN — LIVE: real WO/part resolve → WO detail. */}
                <Tile className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        <ScanLine className="h-3.5 w-3.5" /> Scan
                    </div>
                    <ScanBox />
                </Tile>

                {/* NOTIFICATIONS — LIVE: the in-app notification feed (awareness
                    surface). "Got it" marks the item read. */}
                <Tile className="md:col-span-2 border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-blue-700 dark:text-blue-300">
                        <Megaphone className="h-3.5 w-3.5" /> Notifications
                        {unread.length > 0 && (
                            <Badge className="bg-blue-600 text-white hover:bg-blue-600">{unread.length} new</Badge>
                        )}
                    </div>
                    <div className="mt-2 space-y-1.5 text-sm">
                        {unread.length === 0 && (
                            <p className="text-muted-foreground">All caught up — nothing new.</p>
                        )}
                        {unread.map((n) => (
                            <div key={n.id} className="flex items-start gap-2">
                                <div className="min-w-0 flex-1">
                                    <b>{n.rendered_subject}</b>
                                    {n.rendered_body_text ? <span className="text-muted-foreground"> — {n.rendered_body_text}</span> : null}
                                    {n.rendered_action_url ? (
                                        <a href={n.rendered_action_url} className="ml-1 whitespace-nowrap text-blue-700 underline-offset-4 hover:underline dark:text-blue-300">Open</a>
                                    ) : null}
                                </div>
                                <Button
                                    size="sm" variant="ghost" className="h-7 shrink-0 px-2 text-xs"
                                    disabled={markRead.isPending}
                                    onClick={() => markRead.mutate(n.id)}
                                >
                                    Got it
                                </Button>
                            </div>
                        ))}
                    </div>
                </Tile>

                {/* SHIFT NOTES — LIVE: human-authored floor handoff for this
                    operator. "Got it" acknowledges (a receipt), distinct from
                    the feed's mark-read. */}
                <Tile className="md:col-span-2 border-indigo-200 bg-indigo-50 dark:border-indigo-900 dark:bg-indigo-950">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                        <StickyNote className="h-3.5 w-3.5" /> Shift notes
                        {shiftNotes.length > 0 && (
                            <Badge className="bg-indigo-600 text-white hover:bg-indigo-600">{shiftNotes.length}</Badge>
                        )}
                    </div>
                    {shiftNotes.length === 0 && (
                        <p className="mt-2 text-sm text-muted-foreground">No shift notes right now.</p>
                    )}
                    {/* Must-acknowledge notes lead, called out — a compulsory action. */}
                    {mustAckNotes.length > 0 && (
                        <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 dark:border-amber-800 dark:bg-amber-950">
                            <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                                <AlertTriangle className="h-3 w-3" /> Must acknowledge
                            </div>
                            <div className="space-y-1.5 text-sm">{mustAckNotes.map(noteRow)}</div>
                        </div>
                    )}
                    {infoNotes.length > 0 && (
                        <div className="mt-2 space-y-1.5 text-sm">{infoNotes.map(noteRow)}</div>
                    )}
                </Tile>

                {/* THEN — LIVE: the next ranked ready rows from the work queue.
                    Blocked count is a real aggregate (is_held rows). */}
                <Tile className="md:col-span-2 h-full">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Then</div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                        {thenRows.length === 0 && (
                            <p className="col-span-2 text-xs text-muted-foreground">
                                {heroRow ? "Nothing else queued behind this." : "Queue is empty."}
                            </p>
                        )}
                        {thenRows.map((row, i) => (
                            <button
                                key={`${row.work_order}-${row.step}`}
                                onClick={() => navigate({
                                    to: "/workorder/$workOrderId",
                                    params: { workOrderId: String(row.work_order) },
                                })}
                                className="rounded-lg border p-3 text-left transition-colors hover:bg-accent"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold">{i + 2}</span>
                                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{row.step_name}</span>
                                    {priorityTone(row.priority) && (
                                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${priorityTone(row.priority)}`}>
                                            {priorityLabel(row.priority)}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1 truncate font-mono text-xs text-muted-foreground">
                                    {row.work_order_erp_id} · {row.qty_ready} pc{row.qty_ready === 1 ? "" : "s"}
                                    {row.machine_name ? ` · ${row.machine_name}` : ""}
                                    {row.scheduled_start ? ` · planned ${new Date(row.scheduled_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
                                    {row.earliest_entered_at ? ` · waiting ${formatDistanceToNow(new Date(row.earliest_entered_at))}` : ""}
                                </div>
                            </button>
                        ))}
                    </div>
                    <div className="mt-2 flex items-center gap-4">
                        {blockedRows.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                                {blockedRows.length} job{blockedRows.length === 1 ? "" : "s"} blocked — waiting on holds
                            </span>
                        )}
                        <button
                            className="ml-auto text-xs text-muted-foreground underline-offset-4 hover:underline"
                            onClick={() => say("Would open the full ready queue (the work-queue list)")}
                        >
                            See everything ready ({readyRows.length})
                        </button>
                    </div>
                </Tile>

                {/* REPORT A PROBLEM — LIVE for a machine (the downtime dialog, my station's
                    machines); the job branch stays Preview (blocker routing, 10/21).
                    CALL MY LEAD stays Preview by decision. */}
                <Tile className="h-full flex items-stretch p-0">
                    <button
                        onClick={() => { setProblemBranch(null); setProblemOpen(true); }}
                        className="flex w-full flex-col items-center justify-center gap-2 rounded-xl p-4 transition-colors hover:bg-accent"
                    >
                        <AlertTriangle className="h-8 w-8 text-amber-600" />
                        <span className="text-sm font-medium">Report a problem</span>
                    </button>
                </Tile>
                <PreviewLock>
                    <Tile className="h-full flex items-stretch p-0">
                        <button
                            onClick={() => say("Would ping your shift lead")}
                            className="flex w-full flex-col items-center justify-center gap-2 rounded-xl p-4 transition-colors hover:bg-accent"
                        >
                            <Hand className="h-8 w-8" />
                            <span className="text-sm font-medium">Call my lead</span>
                        </button>
                    </Tile>
                </PreviewLock>
            </div>

            {/* YOUR FLAGS — needs the blocker lifecycle. Preview. */}
            {flags.length > 0 && (
                <PreviewLock>
                    <div className="space-y-1 rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                        {flags.map((f) => (
                            <div key={f.id} className="flex items-center gap-2">
                                <AlertTriangle className="h-3 w-3 shrink-0 text-amber-600" />
                                <span className="min-w-0 flex-1 truncate">
                                    <b className="text-foreground">{f.label}</b> · {f.wo} → {f.owner}
                                </span>
                                <span className="shrink-0">
                                    {f.seenBy ? `Seen by ${f.seenBy}` : "Routed — not seen yet"} · {f.elapsed}
                                </span>
                            </div>
                        ))}
                    </div>
                </PreviewLock>
            )}

            {/* Undo strip — LIVE: my last completed step (StepExecutions/my_day). Undo is
                the Parts rollback: inside the step's undo window it's done at once; past
                it (or where the step always wants approval) it goes for approval (10/26).
                Offered only while the part hasn't moved past that step. Flag opens a new
                quality report on that part at that step. */}
            {lastFinished && (
                <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-muted-foreground">
                    <span>
                        Finished {formatDistanceToNow(new Date(lastFinished.finished_at))} ago: {lastFinished.step_name}
                        {lastFinished.part_erp_id ? <> · <span className="font-mono">{lastFinished.part_erp_id}</span></> : null}
                    </span>
                    {lastFinished.undoable && lastFinished.part_id && (
                        <ReasonActionButton
                            label={lastFinished.undo_needs_approval ? "Undo (needs approval)" : "Undo"}
                            variant="ghost"
                            title={`Undo ${lastFinished.step_name}?`}
                            description={lastFinished.undo_needs_approval
                                ? "This is past the step's undo window, so the undo is sent to QA for approval. Say why it needs undoing."
                                : "The part goes back to this step so it can be done again. Say why; the reason is kept."}
                            confirmLabel={lastFinished.undo_needs_approval ? "Send for approval" : "Undo"}
                            done={(result) => (result as { requires_approval?: boolean })?.requires_approval
                                ? "Sent for approval"
                                : `Undone — ${lastFinished.part_erp_id ?? "the part"} is back at ${lastFinished.step_name}`}
                            run={(reason) => api.api_Parts_rollback_create(
                                { reason },
                                { params: { id: lastFinished.part_id! }, headers: { "X-CSRFToken": getCookie("csrftoken") ?? "" } },
                            )}
                        />
                    )}
                    {lastFinished.part_id && (
                        <Button asChild size="sm" variant="ghost">
                            <Link to="/editor/quality-reports/new" search={{ part: lastFinished.part_id, step: lastFinished.step_id }}>
                                Flag
                            </Link>
                        </Button>
                    )}
                </div>
            )}

            {/* Day recap — LIVE: steps I completed today and the findings (FAIL / REPAIR reports) I raised
                today, on the plant clock (StepExecutions/my_day). */}
            <p className="mx-auto w-fit text-center text-sm text-muted-foreground">
                Today: <b className="text-foreground">{myDay?.completed_today ?? "—"}</b> step{myDay?.completed_today === 1 ? "" : "s"} completed
                {" · "}<b className="text-foreground">{myDay?.flagged_today ?? "—"}</b> flagged for QA
            </p>

            {/* Preview dialogs — triggers live under glass, so these never open in
                the shell; kept so the planned flow stays visible in the source. */}
            {/* Shown at clock-in while any must-acknowledge note is unread; no way out
                but acknowledging — that is what "must acknowledge" means. */}
            <Dialog open={ackAtClockIn && mustAckNotes.length > 0} onOpenChange={() => {}}>
                <DialogContent className="sm:max-w-lg" showCloseButton={false}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle>Before you start</DialogTitle>
                        <DialogDescription>
                            Your lead has notes you need to acknowledge for this shift.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">{mustAckNotes.map(noteRow)}</div>
                </DialogContent>
            </Dialog>

            <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Can't run {heroRow?.step_name ?? "this step"}?</DialogTitle>
                        <DialogDescription>
                            Pick why. It routes straight to the owner, leaves your queue, and your next job appears immediately.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 gap-2">
                        {BLOCK_REASONS.map((r) => (
                            <Button key={r.key} size="lg" variant="outline" className="h-14 text-base"
                                onClick={() => blockUpNext(r)}>
                                <XCircle className="mr-3 h-5 w-5 shrink-0 text-muted-foreground" />
                                <span className="flex-1 text-left">{r.label}</span>
                                <span className="text-xs font-normal text-muted-foreground">→ {r.owner}</span>
                            </Button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={problemOpen} onOpenChange={(v) => { setProblemOpen(v); if (!v) setProblemBranch(null); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Report a problem</DialogTitle>
                        <DialogDescription>
                            {problemBranch === null ? "What kind of problem?" : problemBranch === "machine" ? "What's wrong with the machine?" : "Why can't the job run?"}
                        </DialogDescription>
                    </DialogHeader>
                    {problemBranch === null ? (
                        <div className="grid grid-cols-1 gap-2">
                            <Button size="lg" variant="outline" className="h-16 justify-start text-base" onClick={() => setProblemBranch("machine")}>
                                <Wrench className="mr-3 h-5 w-5 text-muted-foreground" /> Machine problem
                            </Button>
                            <Button size="lg" variant="outline" className="h-16 justify-start text-base" onClick={() => setProblemBranch("job")}>
                                <XCircle className="mr-3 h-5 w-5 text-muted-foreground" /> Can't run a job
                                <Badge variant="secondary" className="ml-auto gap-1 text-[10px]"><Lock className="h-3 w-3" /> Preview</Badge>
                            </Button>
                        </div>
                    ) : problemBranch === "machine" ? (
                        <div className="grid grid-cols-1 gap-2">
                            {MACHINE_REASONS.map((r) => (
                                <Button key={r.label} size="lg" variant="outline" className="h-14 justify-start text-base"
                                    onClick={() => { setProblemOpen(false); setProblemBranch(null); setDowntime(r); }}>
                                    {r.label}
                                </Button>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-2">
                            {BLOCK_REASONS.map((r) => (
                                <Button key={r.key} size="lg" variant="outline" className="h-14 justify-start text-base"
                                    onClick={() => { setProblemOpen(false); setProblemBranch(null); say(`Would open a job blocker: "${r.label}" (${r.owner} notified)`); }}>
                                    {r.label}
                                </Button>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ReportExceptionDialog
                open={downtime !== null}
                onOpenChange={(v) => { if (!v) setDowntime(null); }}
                downtimeOnly
                equipmentIds={stationEquipmentIds.length ? stationEquipmentIds : undefined}
                initialCategory={downtime?.category}
                initialReason={downtime?.label}
                workOrders={downtimeWorkOrders}
                initialWorkOrderId={runs.length === 1 && runWorkOrders.length === 1 ? runWorkOrders[0] : undefined}
            />
        </div>
    );
}

export default OperatorHomePage;
